import { useState, useCallback } from 'react';
import type { UseFormGetValues, UseFormTrigger } from 'react-hook-form';
import type { HnpUploadTab } from '../../services/api/types.gen';
import { getTodayInPT } from '../../utils/dateUtils';
import { client, getReferralDocs } from '../../services/api';

export interface KinnserAttachment {
  client_file: string | null;
  server_file: string | null;
  error: string;
}

export interface KinnserOnboardPatientResponse {
  patient_creation: {
    workflow_id: string;
    success: boolean;
    message: string;
  };
  task_creation: {
    patient_task_key: string;
    task_name: string;
  };
  upload: {
    attachments: KinnserAttachment[];
    patient_task_key: string;
  };
}

export interface S3File {
  url: string;
  filename: string;
}

interface ReferralDocument {
  id: string;
  file_name: string;
  url: string;
}

interface UseKinnserOnboardingOptions<
  T extends {
    kinnser_clinic_id?: string | null;
    kinnser_branch_id?: string | null;
  },
> {
  intakeId: string | undefined;
  trigger: UseFormTrigger<T>;
  getValues: UseFormGetValues<T>;
  tabName: HnpUploadTab | null | undefined;
  taskName: string | null | undefined;
  s3Files: S3File[];
  // Optional task/upload configuration
  taskDate?: string; // YYYY-MM-DD format
  patientLocation?: string;
}

interface UseKinnserOnboardingReturn {
  onboardingInKinnser: boolean;
  onboardResult: KinnserOnboardPatientResponse | null;
  handleOnboardInKinnser: () => Promise<void>;
}

/**
 * Hook for onboarding patients in Kinnser (creates patient, task, and uploads attachment).
 * Manages the onboarding state and handles the API call.
 */
export function useKinnserOnboarding<
  T extends {
    kinnser_clinic_id?: string | null;
    kinnser_branch_id?: string | null;
  },
>({
  intakeId,
  trigger,
  getValues,
  tabName,
  taskName,
  s3Files,
  taskDate,
  patientLocation,
}: UseKinnserOnboardingOptions<T>): UseKinnserOnboardingReturn {
  const [creatingInKinnser, setOnboardingInKinnser] = useState(false);
  const [onboardResult, setOnboardResult] =
    useState<KinnserOnboardPatientResponse | null>(null);

  const handleOnboardInKinnser = useCallback(async () => {
    if (!intakeId) return;

    // Validate form before calling API
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    // Validate that if any H&P field is specified, all must be specified
    const tabNameProvided = tabName !== null && tabName !== undefined;
    const taskNameProvided =
      taskName !== null && taskName !== undefined && taskName.trim() !== '';
    const taskDateProvided =
      taskDate !== null && taskDate !== undefined && taskDate.trim() !== '';

    // If tab_name and task_name are provided but task_date is not, default to today
    let taskDateValue: string | null = taskDate || null;
    if (tabNameProvided && taskNameProvided && !taskDateProvided) {
      taskDateValue = getTodayInPT(); // YYYY-MM-DD format in Pacific Time
    }

    // Count how many fields are provided (after defaulting task_date if needed)
    const finalTaskDateProvided = taskDateValue !== null;
    const providedCount = [
      tabNameProvided,
      taskNameProvided,
      finalTaskDateProvided,
    ].filter(Boolean).length;

    // If any field is provided, all must be provided
    if (providedCount > 0 && providedCount < 3) {
      setOnboardResult({
        patient_creation: {
          workflow_id: '',
          success: false,
          message:
            'If any of H&P upload tab, H&P upload task, or task date is specified, all must be specified.',
        },
        task_creation: {
          patient_task_key: '',
          task_name: taskName || '',
        },
        upload: {
          attachments: [],
          patient_task_key: '',
        },
      });
      return;
    }

    // Get clinic_id and branch_id from form values
    const formValues = getValues();
    const clinicId = formValues.kinnser_clinic_id;
    const branchId = formValues.kinnser_branch_id;

    if (!clinicId || !branchId) {
      console.error('Clinic ID and Branch ID are required for onboarding');
      setOnboardResult({
        patient_creation: {
          workflow_id: '',
          success: false,
          message: 'Clinic ID and Branch ID are required',
        },
        task_creation: {
          patient_task_key: '',
          task_name: taskName || '',
        },
        upload: {
          attachments: [],
          patient_task_key: '',
        },
      });
      return;
    }

    setOnboardingInKinnser(true);
    setOnboardResult(null);

    try {
      // Validate that we have at least one file
      if (!s3Files || s3Files.length === 0) {
        setOnboardResult({
          patient_creation: {
            workflow_id: '',
            success: false,
            message: 'At least one document is required for onboarding',
          },
          task_creation: {
            patient_task_key: '',
            task_name: taskName || '',
          },
          upload: {
            attachments: [],
            patient_task_key: '',
          },
        });
        setOnboardingInKinnser(false);
        return;
      }

      // Regenerate presigned URLs to ensure they haven't expired
      // This prevents 403 errors when the user has been on the page for a long time
      let freshS3Files: S3File[] = [];
      try {
        const urlResponse = await getReferralDocs({
          path: { intake_id: intakeId },
          client,
        });
        const documents = urlResponse.data?.documents;
        if (documents && Array.isArray(documents) && documents.length > 0) {
          freshS3Files = (documents as unknown as ReferralDocument[]).map(
            doc => ({
              url: doc.url,
              filename: doc.file_name,
            })
          );
        } else {
          console.error('Failed to get fresh presigned URLs');
          setOnboardResult({
            patient_creation: {
              workflow_id: '',
              success: false,
              message: 'Failed to generate presigned URLs for documents',
            },
            task_creation: {
              patient_task_key: '',
              task_name: taskName || '',
            },
            upload: {
              attachments: [],
              patient_task_key: '',
            },
          });
          setOnboardingInKinnser(false);
          return;
        }
      } catch (urlError) {
        console.error('Error fetching fresh presigned URLs:', urlError);
        setOnboardResult({
          patient_creation: {
            workflow_id: '',
            success: false,
            message: 'Failed to generate presigned URLs for documents',
          },
          task_creation: {
            patient_task_key: '',
            task_name: taskName || '',
          },
          upload: {
            attachments: [],
            patient_task_key: '',
          },
        });
        setOnboardingInKinnser(false);
        return;
      }

      // Call the new onboard endpoint
      // Using fetch directly until SDK is regenerated with the new endpoint
      const apiBaseUrl =
        import.meta.env.VITE_API_URL || 'http://localhost:8001';
      const response = await fetch(
        `${apiBaseUrl}/kinnser/patient/onboard/${clinicId}/${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            intake_id: intakeId,
            tab_name: tabName || null,
            task_name: taskName || null,
            task_date: taskDateValue || null,
            patient_location: patientLocation || null,
            s3_files: freshS3Files,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `HTTP error! status: ${response.status}`
        );
      }

      const data: KinnserOnboardPatientResponse = await response.json();
      setOnboardResult(data);
    } catch (error) {
      console.error('Error onboarding patient in Kinnser:', error);
      setOnboardResult({
        patient_creation: {
          workflow_id: '',
          success: false,
          message:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred. Please try again.',
        },
        task_creation: {
          patient_task_key: '',
          task_name: taskName || '',
        },
        upload: {
          attachments: [],
          patient_task_key: '',
        },
      });
    } finally {
      setOnboardingInKinnser(false);
    }
  }, [
    intakeId,
    trigger,
    getValues,
    tabName,
    taskName,
    taskDate,
    patientLocation,
    s3Files,
  ]);

  return {
    onboardingInKinnser: creatingInKinnser,
    onboardResult: onboardResult,
    handleOnboardInKinnser: handleOnboardInKinnser,
  };
}
